import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import ko from './locales/ko.json'
import {
  DEFAULT_LOCALE,
  LOCALE_LOADERS,
  SUPPORTED_LOCALE_CODES,
  type SupportedLocaleCode,
} from './locales/config'

const loadedLanguages = new Set<string>([DEFAULT_LOCALE])

const normalizeLanguage = (language?: string) => {
  if (!language) {
    return DEFAULT_LOCALE
  }

  const normalized = language.split('-')[0]
  return SUPPORTED_LOCALE_CODES.includes(normalized as SupportedLocaleCode)
    ? (normalized as SupportedLocaleCode)
    : DEFAULT_LOCALE
}

const ensureLanguageResources = async (language?: string) => {
  const normalizedLanguage = normalizeLanguage(language)

  if (loadedLanguages.has(normalizedLanguage)) {
    return
  }

  const loader = LOCALE_LOADERS[normalizedLanguage]
  if (!loader) {
    return
  }

  const module = await loader()
  i18n.addResourceBundle(
    normalizedLanguage,
    'translation',
    module.default,
    true,
    true
  )
  loadedLanguages.add(normalizedLanguage)
}

const isTestEnv =
  (typeof process !== 'undefined' &&
    (process as { env?: Record<string, unknown> }).env?.VITEST === 'true') ||
  (typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { MODE?: string } }).env?.MODE === 'test')

if (!isTestEnv) {
  void i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        ko: { translation: ko },
      },
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: SUPPORTED_LOCALE_CODES,
      load: 'languageOnly',
      partialBundledLanguages: true,
      debug: false,
      interpolation: {
        escapeValue: false,
      },
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
      },
    })
    .then(async () => {
      await ensureLanguageResources(i18n.resolvedLanguage || i18n.language)

      i18n.on('languageChanged', (language) => {
        void ensureLanguageResources(language)
      })
    })
}

export default i18n
